import { validateCpfAndCnpj } from 'v2/helpers/cpfCnpj';
import { CurrencyId } from 'v2/interfaces/financial';
import * as Yup from 'yup';

export enum accountBankType {
  CONTA_CORRENTE = 'CC',
  CONTA_POUPANCA = 'CP',
}

export type NewBankAccount = {
  id: number;
  account_nature: 'F' | 'J';
  personal_document: 'RG' | 'CNH';
  currency: CurrencyId;
  type: accountBankType.CONTA_CORRENTE | accountBankType.CONTA_POUPANCA;
  bank_id: string;
  operation_id: string;
  document_number: string;
  name: string;
  agency: string;
  agency_digit: string;
  account: string;
  account_digit: string;
  notes?: string;
  bank_receipt_image: File | null;
  rg_front_image: File | null;
  rg_back_image: File | null;
  cnh_image: File | null;
  selfie_video: File | null;
  verify_code: string | null;
};

export const CAIXA_ECONOMICA_ID = '104';
export const BANCO_DO_BRASIL_ID = '001';

const accountNatureTypes = [
  {
    value: 'F',
    label: 'Conta pessoa Física',
  },
  {
    value: 'J',
    label: 'Conta pessoa Jurídica',
  },
];

const accountTypes = [
  {
    value: accountBankType.CONTA_CORRENTE,
    label: 'Conta corrente',
  },
  {
    value: accountBankType.CONTA_POUPANCA,
    label: 'Conta poupança',
  },
];

const currencyTypes = [
  {
    value: CurrencyId.BRL,
    label: 'R$',
  },
  {
    value: CurrencyId.USD,
    label: '$',
  },
];

const initialValues: NewBankAccount = {
  id: null,
  account_nature: 'J',
  currency: 0,
  type: accountBankType.CONTA_CORRENTE,
  bank_id: 'not-selected',
  operation_id: 'not-selected',
  document_number: '',
  name: '',
  agency: '',
  agency_digit: '',
  account: '',
  account_digit: '',
  notes: '',
  personal_document: 'RG',
  bank_receipt_image: null,
  rg_front_image: null,
  rg_back_image: null,
  cnh_image: null,
  selfie_video: null,
  verify_code: '',
};

const isSelected = (value: string) => value !== 'not-selected';
const testFileFormat = (formats: string[]) => (file: File) => file ? formats.includes(file.type) : true;
const testFileSize = (maxSizeInMB: number) => (file: File) => {
  if (!file) return true;
  const fileSizeInMB = file.size / 1024 / 1024;
  return fileSizeInMB <= maxSizeInMB;
};

const validationsSchema = {
  national: Yup.object().shape({
    account_nature: Yup.string().required('A natureza da conta é obrigatória'),
    name: Yup.string().required('O nome é obrigatório'),
    currency: Yup.number().required('O tipo da moeda é obrigatório'),
    type: Yup.string().required('O tipo da conta é obrigatório'),
    bank_id: Yup.string().required('Selecione um banco antes de continuar').test('invalid', 'Selecione um banco antes de continuar', isSelected),
    operation_id: Yup.string().when('bank_id', {
      is: CAIXA_ECONOMICA_ID,
      then: Yup.string()
        .required('Selecione uma operação antes de continuar')
        .test('invalid', 'Selecione uma operação antes de continuar', isSelected),
    }),
    document_number: Yup.string().required('O CPF/CNPJ é obrigatório').test('invalid', 'CPF ou CNPJ inválido', validateCpfAndCnpj),
    agency: Yup.string().required('Informe o número da agência').matches(/^\d+$/, 'Campo aceita apenas números'),
    agency_digit: Yup.string().matches(/^[0-9]+$/, 'Campo aceita apenas números'),
    account: Yup.string().required('Informe o número da conta').matches(/^\d+$/, 'Campo aceita apenas números'),
    account_digit: Yup.string()
      .required('Informe o dígito da conta')
      .when('bank_id', {
        is: BANCO_DO_BRASIL_ID,
        then: Yup.string().matches(/[0-9xX]+$/, 'Campo aceita apenas números e "X"'),
        otherwise: Yup.string().matches(/^[0-9]+$/, 'Campo aceita apenas números'),
      }),
    rg_front_image: Yup.mixed()
      .when(['id', 'personal_document'], {
        is: (id: number, personal_document: string) => typeof id !== 'number' && personal_document === 'RG',
        then: Yup.mixed().required('Por favor, adicione a imagem da frente do RG'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    rg_back_image: Yup.mixed()
      .when(['id', 'personal_document'], {
        is: (id: number, personal_document: string) => typeof id !== 'number' && personal_document === 'RG',
        then: Yup.mixed().required('Por favor, adicione a imagem do verso do RG'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    cnh_image: Yup.mixed()
      .when(['id', 'personal_document'], {
        is: (id: number, personal_document: string) => typeof id !== 'number' && personal_document === 'CNH',
        then: Yup.mixed().required('Por favor, adicione a imagem da CNH'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    selfie_video: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Por favor, adicione o vídeo selfie'),
      })
      .test(
        'Video Format',
        'Informe um arquivo com um desses formatos: .avi, .mp4, .qtff, .wmv, .mkv, .webm',
        testFileFormat([
          'video/avi',
          'video/x-msvideo',
          'video/x-flv',
          'video/mp4',
          'video/quicktime',
          'video/x-ms-wmv',
          'video/x-matroska',
          'video/webm',
        ])
      )
      .test('Image size', 'O arquivo deve ter no máximo 35MB', testFileSize(35)),
    bank_receipt_image: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Informe o comprovante bancário'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    verify_code: Yup.string().matches(/^\d+$/, 'Campo aceita apenas números').length(6),
  }),
  crossborder: Yup.object().shape({
    account_nature: Yup.string().required('A natureza da conta é obrigatória'),
    name: Yup.string().required('O nome é obrigatório'),
    currency: Yup.number().required('O tipo da moeda é obrigatório'),
    type: Yup.string().required('O tipo da conta é obrigatório'),
    bank_id: Yup.string().required('Selecione um banco antes de continuar').test('invalid', 'Selecione um banco antes de continuar', isSelected),
    document_number: Yup.string().required('Número de documento é obrigatório'),
    agency: Yup.string().required('Informe o número da agência').matches(/^\d+$/, 'Campo aceita apenas números'),
    account: Yup.string().required('Informe o número da conta').matches(/^\d+$/, 'Campo aceita apenas números'),
    notes: Yup.string(),
    rg_front_image: Yup.mixed()
      .when(['id', 'personal_document'], {
        is: (id: number, personal_document: string) => typeof id !== 'number' && personal_document === 'RG',
        then: Yup.mixed().required('Por favor, adicione a imagem da frente do RG'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    rg_back_image: Yup.mixed()
      .when(['id', 'personal_document'], {
        is: (id: number, personal_document: string) => typeof id !== 'number' && personal_document === 'RG',
        then: Yup.mixed().required('Por favor, adicione a imagem do verso do RG'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    cnh_image: Yup.mixed()
      .when(['id', 'personal_document'], {
        is: (id: number, personal_document: string) => typeof id !== 'number' && personal_document === 'CNH',
        then: Yup.mixed().required('Por favor, adicione a imagem da CNH'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    selfie_video: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Por favor, adicione o vídeo selfie'),
      })
      .test(
        'Video Format',
        'Informe um arquivo com um desses formatos: .avi, .mp4, .qtff, .wmv, .mkv, .webm',
        testFileFormat([
          'video/avi',
          'video/x-msvideo',
          'video/x-flv',
          'video/mp4',
          'video/quicktime',
          'video/x-ms-wmv',
          'video/x-matroska',
          'video/webm',
        ])
      )
      .test('Image size', 'O arquivo deve ter no máximo 35MB', testFileSize(35)),
    bank_receipt_image: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Informe o comprovante bancário'),
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    verify_code: Yup.string().matches(/^\d+$/, 'Campo aceita apenas números').length(6),
  }),
};

const fieldsByStep = {
  national: [
    [
      'account_nature',
      'name',
      'currency',
      'type',
      'bank_id',
      'operation_id',
      'document_number',
      'agency',
      'agency_digit',
      'account',
      'account_digit',
    ],
    ['cnh_image', 'rg_front_image', 'rg_back_image', 'selfie_video'],
    ['bank_receipt_image'],
    [],
    ['verify_code'],
  ],
  crossborder: [
    ['account_nature', 'name', 'currency', 'type', 'bank_id', 'document_number', 'agency', 'account', 'notes'],
    ['cnh_image', 'rg_front_image', 'rg_back_image', 'selfie_video'],
    ['bank_receipt_image'],
    [],
    ['verify_code'],
  ],
};

const getSchema = (isCrossborder: boolean) => (isCrossborder ? validationsSchema.crossborder : validationsSchema.national);

export { initialValues, getSchema, accountTypes, currencyTypes, accountNatureTypes, fieldsByStep };
